/* eslint-disable jsdoc/require-param-type */
/* eslint-disable jsdoc/require-param-description */
import React, { useState, useEffect } from "react";
import {
  Button,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { makeStyles } from "@material-ui/core/styles";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  countryCodes,
  dealerIdTypes,
  tShirtSizes,
} from "../../../../../components/shared/constants";
import { supabase } from "../../../../../supabase";
import { useAuth } from "../../../../../contexts/AuthContext";

const useStyles = makeStyles(theme => ({
  formContainer: {
    marginTop: theme.spacing(2),
  },
  buttons: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(3),
  },
  buttonRight: {
    marginLeft: "auto",
  },
  radioGroup: {
    flexDirection: "row",
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
}));

/**
 * PersonalInfoForm component with all required fields
 * @param root0
 * @param root0.formData
 * @param root0.onSave
 * @param root0.onBack
 * @param root0.onNext
 * @param root0.submitLabel
 * @param root0.showBackButton
 * @param root0.isEditing
 */
export default function PersonalInfoForm({
  formData,
  onSave,
  onBack,
  onNext,
  submitLabel = "Save",
  showBackButton = false,
  isEditing = false,
}) {
  const classes = useStyles();
  const { currentUser } = useAuth();

  // Local state for form values
  const [values, setValues] = useState({
    firstName: formData.firstName || "",
    middleName: formData.middleName || "",
    surname: formData.surname || "",
    dateOfBirth: formData.dateOfBirth,
    refsGender: formData.refsGender || "M",
    refsIdentityDocType: formData.refsIdentityDocType || "RSAID",
    identificationNo: formData.identificationNo || "",
    nationality: formData.nationality || "ZAF",
    operatorId: formData.operatorId || "",
    companyName: formData.companyName || "",
    email: formData.email || "",
    phone: formData.phone || "",
    altPhone: formData.altPhone || "",
    passportIssueDate: formData.passportIssueDate,
    passportExpiryDate: formData.passportExpiryDate,
    refsShirtSize: formData.refsShirtSize || "",
    parentDealer: formData.parentDealer || currentUser?.id || null,
  });

  const [errors, setErrors] = useState({});
  const [parentDealerOptions, setParentDealerOptions] = useState([]);
  const [loadingParentDealers, setLoadingParentDealers] = useState(false);

  // Fetch available parent dealers (downstream users) - only if not editing
  useEffect(() => {
    if (!isEditing) {
      fetchParentDealerOptions();
    }
  }, [isEditing]);

  useEffect(() => {
    setValues({
      firstName: formData.firstName || "",
      middleName: formData.middleName || "",
      surname: formData.surname || "",
      dateOfBirth: formData.dateOfBirth,
      refsGender: formData.refsGender || "M",
      refsIdentityDocType: formData.refsIdentityDocType || "RSAID",
      identificationNo: formData.identificationNo || "",
      nationality: formData.nationality || "ZAF",
      operatorId: formData.operatorId || "",
      companyName: formData.companyName || "",
      email: formData.email || "",
      phone: formData.phone || "",
      altPhone: formData.altPhone || "",
      passportIssueDate: formData.passportIssueDate,
      passportExpiryDate: formData.passportExpiryDate,
      refsShirtSize: formData.refsShirtSize || "",
      parentDealer: formData.parentDealer || currentUser?.id || null,
    });
  }, [formData, currentUser]);

  const fetchParentDealerOptions = async () => {
    setLoadingParentDealers(true);
    try {
      // Get downstream users that can be parents
      const { data, error } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("users_master")
        .select("id, first_name, middle_name, surname")
        .eq("refs_user_lifecycle_status_id", 2) // Active users only
        .order("first_name");

      if (error) throw error;

      const options = data.map(user => ({
        id: user.id,
        label: `Z-${user.id}: ${user.first_name} ${
          user.middle_name ? `${user.middle_name} ` : ""
        }${user.surname}`,
        firstName: user.first_name,
        surname: user.surname,
      }));

      setParentDealerOptions(options);
    } catch (error) {
      console.error("Error fetching parent dealers:", error);
    } finally {
      setLoadingParentDealers(false);
    }
  };

  // Handle input changes
  const handleChange = field => event => {
    const { value } = event.target;
    const newValues = { ...values, [field]: value };
    
    setValues(newValues);

    // Auto-save to parent immediately
    if (onSave) {
      const dataToSave = {
        ...formData,
        ...newValues,
      };
      onSave(dataToSave);
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // Handle date changes
  const handleDateChange = field => date => {
    const timestamp = date ? Math.floor(date.getTime() / 1000) : null;
    const newValues = { ...values, [field]: timestamp };
    
    setValues(newValues);

    // Auto-save to parent immediately
    if (onSave) {
      const dataToSave = {
        ...formData,
        ...newValues,
      };
      onSave(dataToSave);
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // Handle parent dealer selection
  const handleParentDealerChange = (event, newValue) => {
    const newValues = {
      ...values,
      parentDealer: newValue ? newValue.id : null,
    };
    
    setValues(newValues);

    // Auto-save to parent immediately
    if (onSave) {
      const dataToSave = {
        ...formData,
        ...newValues,
      };
      onSave(dataToSave);
    }

    if (errors.parentDealer) {
      setErrors(prev => ({ ...prev, parentDealer: "" }));
    }
  };

  // Basic validation
  const validateForm = () => {
    const newErrors = {};

    if (!values.firstName?.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!values.surname?.trim()) newErrors.surname = "Surname is required";
    if (!values.dateOfBirth) {
      newErrors.dateOfBirth = "Date of birth is required";
    }
    if (!values.identificationNo?.trim()) {
      newErrors.identificationNo = "ID/Passport number is required";
    }
    if (!values.phone?.trim()) newErrors.phone = "Phone number is required";

    // Basic email format check
    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Basic phone format check
    if (values.phone && !/^0\d{9}$/.test(values.phone)) {
      newErrors.phone = "Phone number must be 10 digits starting with 0";
    }

    // Alt phone validation
    if (values.altPhone?.trim()) {
      if (!/^0\d{9}$/.test(values.altPhone)) {
        newErrors.altPhone =
          "Alternative phone must be 10 digits starting with 0";
      }
      if (values.altPhone === values.phone) {
        newErrors.altPhone =
          "Alternative phone cannot be same as primary phone";
      }
    }

    // Passport validation
    if (values.refsIdentityDocType === "PASSPORT") {
      if (!values.passportIssueDate) {
        newErrors.passportIssueDate = "Passport issue date is required";
      }
      if (!values.passportExpiryDate) {
        newErrors.passportExpiryDate = "Passport expiry date is required";
      }
    }

    return newErrors;
  };

  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});

    // Basic validation
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const data = {
      ...formData, // Keep all existing form data
      ...values, // Override with personal info changes
    };

    // Save data first
    if (onSave) {
      onSave(data);
    }

    // Move to next step
    if (onNext) {
      onNext(data);
    }
  };

  // Get selected parent dealer for display
  const selectedParentDealer = parentDealerOptions.find(
    option => option.id === values.parentDealer
  );

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <form className={classes.formContainer} onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Parent Dealer Selection - Only show when not editing */}
          {!isEditing && (
            <Grid item xs={12}>
              <Typography className={classes.sectionTitle}>
                Manager Selection
              </Typography>
              <Autocomplete
                options={parentDealerOptions}
                getOptionLabel={option => option.label}
                value={selectedParentDealer || null}
                onChange={handleParentDealerChange}
                loading={loadingParentDealers}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Select Manager (Parent User)"
                    variant="outlined"
                    placeholder={
                      currentUser
                        ? `Default: You (Z-${currentUser.id})`
                        : "Select a manager"
                    }
                    error={!!errors.parentDealer}
                    helperText={
                      errors.parentDealer ||
                      "The user who will manage this new user"
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingParentDealers ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          )}

          {/* Personal Information Section */}
          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>
              Personal Information
            </Typography>
          </Grid>

          {/* First Name */}
          <Grid item xs={12} md={4}>
            <TextField
              label="First Name"
              variant="outlined"
              fullWidth
              required
              value={values.firstName}
              onChange={handleChange("firstName")}
              error={!!errors.firstName}
              helperText={errors.firstName}
            />
          </Grid>

          {/* Middle Name */}
          <Grid item xs={12} md={4}>
            <TextField
              label="Middle Name"
              variant="outlined"
              fullWidth
              value={values.middleName}
              onChange={handleChange("middleName")}
            />
          </Grid>

          {/* Surname */}
          <Grid item xs={12} md={4}>
            <TextField
              label="Surname"
              variant="outlined"
              fullWidth
              required
              value={values.surname}
              onChange={handleChange("surname")}
              error={!!errors.surname}
              helperText={errors.surname}
            />
          </Grid>

          {/* Date of Birth */}
          <Grid item xs={12} md={6}>
            <KeyboardDatePicker
              label="Date of Birth"
              inputVariant="outlined"
              format="yyyy/MM/dd"
              fullWidth
              required
              value={
                values.dateOfBirth ? new Date(values.dateOfBirth * 1000) : null
              }
              onChange={handleDateChange("dateOfBirth")}
              error={!!errors.dateOfBirth}
              helperText={errors.dateOfBirth}
              disableFuture
              openTo="year"
              views={["year", "month", "date"]}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarTodayIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Gender */}
          <Grid item xs={12} md={6}>
            <FormControl
              component="fieldset"
              required
              error={!!errors.refsGender}
            >
              <FormLabel component="legend">Gender</FormLabel>
              <RadioGroup
                value={values.refsGender}
                onChange={handleChange("refsGender")}
                className={classes.radioGroup}
              >
                <FormControlLabel value="M" control={<Radio />} label="Male" />
                <FormControlLabel
                  value="F"
                  control={<Radio />}
                  label="Female"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* ID Type */}
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="ID Type"
              variant="outlined"
              fullWidth
              required
              value={values.refsIdentityDocType}
              onChange={handleChange("refsIdentityDocType")}
              error={!!errors.refsIdentityDocType}
              helperText={errors.refsIdentityDocType}
            >
              {dealerIdTypes.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* ID/Passport Number */}
          <Grid item xs={12} md={6}>
            <TextField
              label="ID/Passport Number"
              variant="outlined"
              fullWidth
              required
              value={values.identificationNo}
              onChange={handleChange("identificationNo")}
              error={!!errors.identificationNo}
              helperText={errors.identificationNo}
            />
          </Grid>

          {/* Passport Issue Date (conditionally rendered) */}
          {values.refsIdentityDocType === "PASSPORT" && (
            <Grid item xs={12} md={6}>
              <KeyboardDatePicker
                label="Passport Issue Date"
                inputVariant="outlined"
                format="yyyy/MM/dd"
                fullWidth
                required
                value={
                  values.passportIssueDate
                    ? new Date(values.passportIssueDate * 1000)
                    : null
                }
                onChange={handleDateChange("passportIssueDate")}
                error={!!errors.passportIssueDate}
                helperText={errors.passportIssueDate}
                disableFuture
                openTo="year"
                views={["year", "month", "date"]}
              />
            </Grid>
          )}

          {/* Passport Expiry Date (conditionally rendered) */}
          {values.refsIdentityDocType === "PASSPORT" && (
            <Grid item xs={12} md={6}>
              <KeyboardDatePicker
                label="Passport Expiry Date"
                inputVariant="outlined"
                format="yyyy/MM/dd"
                fullWidth
                required
                value={
                  values.passportExpiryDate
                    ? new Date(values.passportExpiryDate * 1000)
                    : null
                }
                onChange={handleDateChange("passportExpiryDate")}
                error={!!errors.passportExpiryDate}
                helperText={errors.passportExpiryDate}
                disablePast
                openTo="year"
                views={["year", "month", "date"]}
              />
            </Grid>
          )}

          {/* Nationality */}
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Nationality"
              variant="outlined"
              fullWidth
              required
              value={values.nationality}
              onChange={handleChange("nationality")}
              error={!!errors.nationality}
              helperText={errors.nationality}
            >
              {countryCodes.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* T-Shirt Size */}
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="T-Shirt Size"
              variant="outlined"
              fullWidth
              value={values.refsShirtSize}
              onChange={handleChange("refsShirtSize")}
              error={!!errors.refsShirtSize}
              helperText={errors.refsShirtSize}
            >
              {tShirtSizes.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Contact Information Section */}
          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>
              Contact Information
            </Typography>
          </Grid>

          {/* RICA Online Username keep for future implementations */}
          {/* <Grid item xs={12} md={6}>
            <TextField
              label="RICA Online Username"
              variant="outlined"
              fullWidth
              value={values.operatorId}
              onChange={e => {
                const uppercaseValue = e.target.value.toUpperCase();
                setValues(prev => ({ ...prev, operatorId: uppercaseValue }));
              }}
              inputProps={{ maxLength: 5 }}
              helperText="5 characters max"
            />
          </Grid> */}

          {/* Company Name */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Trading / Business Name (optional)"
              variant="outlined"
              fullWidth
              value={values.companyName}
              onChange={handleChange("companyName")}
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12} md={4}>
            <TextField
              label="Email Address"
              variant="outlined"
              type="email"
              fullWidth
              value={values.email}
              onChange={handleChange("email")}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>

          {/* Phone */}
          <Grid item xs={12} md={4}>
            <TextField
              label="Phone Number"
              variant="outlined"
              placeholder="e.g. 0821234567"
              fullWidth
              required
              value={values.phone}
              onChange={handleChange("phone")}
              error={!!errors.phone}
              helperText={errors.phone}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
        </Grid>

        <div className={classes.buttons}>
          {showBackButton && (
            <Button onClick={onBack} disabled={!onBack}>
              Back
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            className={!showBackButton ? classes.buttonRight : ""}
          >
            {submitLabel}
          </Button>
        </div>
      </form>
    </MuiPickersUtilsProvider>
  );
}
